import api from './api';
import type {
  ApiEnvelope,
  MyPerformanceResponse,
  PerformanceCalibration,
  PerformanceCompanyGoal,
  PerformanceDepartmentGoal,
  PerformanceGoal,
  PerformanceParticipantRow,
  PerformanceRatingLevel,
  PerformanceReview,
  PerformanceSchedule,
  PerformanceScheduleDetail,
  PerformanceSchedulePayload,
  PerformanceScheduleSummary,
  PerformanceTouchpoint,
  TeamMemberResponse,
} from '../types/performance.types';

/**
 * Performance module API.
 *
 * The shared `api` helper already handles base URL, auth, JSON parsing, toasts and 401.
 * Every endpoint returns the app's `{ success, data }` envelope; this wrapper unwraps it so
 * pages work with the payload directly.
 */

const unwrap = <T>(response: ApiEnvelope<T> | T): T => {
  if (response && typeof response === 'object' && 'data' in (response as ApiEnvelope<T>)) {
    return (response as ApiEnvelope<T>).data;
  }
  return response as T;
};

const query = (params: Record<string, string | number | undefined>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') q.append(key, String(value));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

export interface PerformanceUploadResult {
  url: string;
  key: string;
  filename: string;
  size: number;
  mimetype: string;
}

export const performanceApi = {
  my: (scheduleId?: number | string) =>
    api
      .get<ApiEnvelope<MyPerformanceResponse>>(`/api/performance/my${query({ scheduleId })}`)
      .then(unwrap),

  teamMember: (userId: number | string, scheduleId?: number | string) =>
    api
      .get<ApiEnvelope<TeamMemberResponse>>(
        `/api/performance/team/${userId}${query({ scheduleId })}`
      )
      .then(unwrap),

  goals: {
    create: (body: Record<string, unknown>) =>
      api.post<ApiEnvelope<PerformanceGoal>>('/api/performance/goals', body).then(unwrap),
    update: (id: number, body: Record<string, unknown>) =>
      api.patch<ApiEnvelope<PerformanceGoal>>(`/api/performance/goals/${id}`, body).then(unwrap),
    withdraw: (id: number, reason: string) =>
      api
        .post<ApiEnvelope<PerformanceGoal>>(`/api/performance/goals/${id}/withdraw`, { reason })
        .then(unwrap),
    updateKeyResult: (goalId: number, keyResultId: number, body: Record<string, unknown>) =>
      api
        .patch<ApiEnvelope<PerformanceGoal>>(
          `/api/performance/goals/${goalId}/key-results/${keyResultId}`,
          body
        )
        .then(unwrap),
  },

  notes: {
    add: (goalId: number, body: { body?: string; attachments?: unknown[] }) =>
      api.post(`/api/performance/goals/${goalId}/notes`, body).then(unwrap),
    update: (goalId: number, noteId: number, body: { body?: string; attachments?: unknown[] }) =>
      api.patch(`/api/performance/goals/${goalId}/notes/${noteId}`, body).then(unwrap),
  },

  plan: {
    reopen: (scheduleId: number | string, userId: number | string, reason: string) =>
      api
        .post<ApiEnvelope<unknown>>(
          `/api/performance/schedules/${scheduleId}/participants/${userId}/reopen-plan`,
          { reason }
        )
        .then(unwrap),
    submit: (scheduleId: number) =>
      api
        .post<ApiEnvelope<{ status: string; submittedAt: string }>>(
          `/api/performance/schedules/${scheduleId}/goal-plan/submit`,
          {}
        )
        .then(unwrap),
  },

  reviews: {
    saveSelf: (
      userId: number | string,
      period: 'MidYear' | 'EndYear',
      body: Record<string, unknown>
    ) =>
      api
        .post<ApiEnvelope<{ submitted: boolean; reviewId: number }>>(
          `/api/performance/reviews/${userId}/self`,
          { period, ...body }
        )
        .then(unwrap),
    reopenSelf: (userId: number | string, period: 'MidYear' | 'EndYear') =>
      api
        .post<ApiEnvelope<{ reopened: boolean }>>(
          `/api/performance/reviews/${userId}/self/reopen`,
          { period }
        )
        .then(unwrap),
    saveManager: (
      userId: number | string,
      period: 'MidYear' | 'EndYear',
      body: Record<string, unknown>
    ) =>
      api
        .post<ApiEnvelope<{ submitted: boolean; reviewId: number }>>(
          `/api/performance/reviews/${userId}/manager`,
          { period, ...body }
        )
        .then(unwrap),
    proceed: (userId: number | string, period: 'MidYear' | 'EndYear', reason: string) =>
      api
        .post<ApiEnvelope<{ proceeded: boolean }>>(
          `/api/performance/reviews/${userId}/proceed`,
          { period, reason }
        )
        .then(unwrap),
  },

  schedules: {
    list: () =>
      api.get<ApiEnvelope<PerformanceSchedule[]>>('/api/performance/schedules').then(unwrap),
    get: (id: number | string) =>
      api
        .get<ApiEnvelope<PerformanceScheduleDetail>>(`/api/performance/schedules/${id}`)
        .then(unwrap),
    create: (body: PerformanceSchedulePayload) =>
      api
        .post<ApiEnvelope<PerformanceScheduleDetail>>('/api/performance/schedules', body)
        .then(unwrap),
    update: (id: number | string, body: Partial<PerformanceSchedulePayload>) =>
      api
        .patch<ApiEnvelope<PerformanceScheduleDetail>>(`/api/performance/schedules/${id}`, body)
        .then(unwrap),
    activate: (id: number | string) =>
      api
        .post<ApiEnvelope<PerformanceSchedule>>(`/api/performance/schedules/${id}/activate`, {})
        .then(unwrap),
    calibration: (id: number | string) =>
      api
        .post<ApiEnvelope<PerformanceSchedule>>(`/api/performance/schedules/${id}/calibration`, {})
        .then(unwrap),
    release: (id: number | string, confirm: string) =>
      api
        .post<ApiEnvelope<{ closed: boolean }>>(`/api/performance/schedules/${id}/release`, {
          confirm,
        })
        .then(unwrap),
  },

  cascade: {
    createCompany: (scheduleId: number | string, body: Record<string, unknown>) =>
      api
        .post<ApiEnvelope<PerformanceCompanyGoal>>(
          `/api/performance/schedules/${scheduleId}/company-goals`,
          body
        )
        .then(unwrap),
    updateCompany: (scheduleId: number | string, goalId: number, body: Record<string, unknown>) =>
      api
        .patch<ApiEnvelope<PerformanceCompanyGoal>>(
          `/api/performance/schedules/${scheduleId}/company-goals/${goalId}`,
          body
        )
        .then(unwrap),
    deleteCompany: (scheduleId: number | string, goalId: number) =>
      api
        .delete<ApiEnvelope<{ deleted: boolean }>>(
          `/api/performance/schedules/${scheduleId}/company-goals/${goalId}`
        )
        .then(unwrap),
    createDepartment: (scheduleId: number | string, body: Record<string, unknown>) =>
      api
        .post<ApiEnvelope<PerformanceDepartmentGoal>>(
          `/api/performance/schedules/${scheduleId}/department-goals`,
          body
        )
        .then(unwrap),
    updateDepartment: (
      scheduleId: number | string,
      goalId: number,
      body: Record<string, unknown>
    ) =>
      api
        .patch<ApiEnvelope<PerformanceDepartmentGoal>>(
          `/api/performance/schedules/${scheduleId}/department-goals/${goalId}`,
          body
        )
        .then(unwrap),
    deleteDepartment: (scheduleId: number | string, goalId: number) =>
      api
        .delete<ApiEnvelope<{ deleted: boolean }>>(
          `/api/performance/schedules/${scheduleId}/department-goals/${goalId}`
        )
        .then(unwrap),
  },

  settings: {
    createCategory: (scheduleId: number | string, body: Record<string, unknown>) =>
      api.post(`/api/performance/schedules/${scheduleId}/categories`, body).then(unwrap),
    updateCategory: (
      scheduleId: number | string,
      categoryId: number,
      body: Record<string, unknown>
    ) =>
      api
        .patch(`/api/performance/schedules/${scheduleId}/categories/${categoryId}`, body)
        .then(unwrap),
    updateLevel: (
      scheduleId: number | string,
      score: number,
      body: Record<string, unknown>
    ) =>
      api
        .patch(`/api/performance/schedules/${scheduleId}/rating-levels/${score}`, body)
        .then(unwrap),
    updateParticipant: (
      scheduleId: number | string,
      userId: number | string,
      body: Record<string, unknown>
    ) =>
      api
        .patch(`/api/performance/schedules/${scheduleId}/participants/${userId}`, body)
        .then(unwrap),
    bulkInclude: (scheduleId: number | string, departmentId: number | string) =>
      api
        .post(`/api/performance/schedules/${scheduleId}/participants/bulk-include`, {
          departmentId,
        })
        .then(unwrap),
  },

  calibration: {
    save: (
      scheduleId: number | string,
      userId: number | string,
      body: { ratings: Record<string, number>; reason: string; overrideScore?: number | null }
    ) =>
      api
        .post<ApiEnvelope<PerformanceCalibration>>(
          `/api/performance/schedules/${scheduleId}/calibration/${userId}`,
          body
        )
        .then(unwrap),
    restore: (scheduleId: number | string, userId: number | string, version: number) =>
      api
        .post<ApiEnvelope<{ version: number; restored: number }>>(
          `/api/performance/schedules/${scheduleId}/calibration/${userId}/restore/${version}`,
          {}
        )
        .then(unwrap),
  },

  touchpoints: {
    list: (scheduleId?: number | string) =>
      api
        .get<ApiEnvelope<PerformanceTouchpoint[]>>(
          `/api/performance/touchpoints${query({ scheduleId })}`
        )
        .then(unwrap),
    create: (body: Record<string, unknown>) =>
      api
        .post<ApiEnvelope<PerformanceTouchpoint>>('/api/performance/touchpoints', body)
        .then(unwrap),
  },

  upload: async (file: File): Promise<PerformanceUploadResult> => {
    const form = new FormData();
    form.append('image', file);
    form.append('context', 'performance');
    return api.post<PerformanceUploadResult>('/api/upload/s3', form);
  },
};

export type { PerformanceParticipantRow, PerformanceRatingLevel, PerformanceReview, PerformanceScheduleSummary };
